import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config/apiConfig';

const RealTimeContext = createContext();

export const useRealTime = () => {
    const context = useContext(RealTimeContext);
    if (!context) {
        throw new Error('useRealTime must be used within a RealTimeProvider');
    }
    return context;
};

export const RealTimeProvider = ({ children }) => {
    const { user, isAuthenticated, checkStatus } = useAuth();
    const [lastUpdate, setLastUpdate] = useState(null);
    const socketRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated || !user?.company_id) {
            if (socketRef.current) {
                console.log('[RealTime] Disconnecting socket...');
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            return;
        }

        // Initialize Socket.io connection
        // Note: API_BASE_URL should contain the protocol (http/https) and domain/port
        const socketInstance = io(API_BASE_URL, {
            transports: ['websocket', 'polling'], // Fallback included for compatibility
            autoConnect: true,
            reconnectionAttempts: 5
        });

        socketInstance.on('connect', () => {
            console.log('[RealTime] Connected to WebSocket server:', socketInstance.id);
            // Join the company-specific room for isolated updates
            socketInstance.emit('join-company', user.company_id);
            // Authenticate the user for targeted messages
            socketInstance.emit('authenticate', user._id || user.id);
        });

        socketInstance.on('data-updated', (data) => {
            console.log('[RealTime] Update received via WebSocket:', data);
            
            // Security check for privilege/status changes
            if (data.type === 'USER_REAUTH') {
                const targetUserId = String(data.userId);
                const currentUserId = String(user._id || user.id);
                
                if (targetUserId === currentUserId) {
                    console.log('[RealTime] Security event for current user. Re-verifying...');
                    checkStatus();
                }
            }

            setLastUpdate(data);
        });

        socketInstance.on('disconnect', (reason) => {
            console.log('[RealTime] WebSocket disconnected:', reason);
        });

        socketInstance.on('connect_error', (error) => {
            console.error('[RealTime] WebSocket connection error:', error.message);
        });

        socketRef.current = socketInstance;

        return () => {
            if (socketInstance) {
                console.log('[RealTime] Cleaning up socket connection...');
                socketInstance.disconnect();
                socketRef.current = null;
            }
        };
    }, [isAuthenticated, user?.company_id, user?._id, user?.id, checkStatus]);

    // Helper to refresh a piece of data manually if needed
    const notifyChange = useCallback((type, details) => {
        setLastUpdate({ type, details, timestamp: new Date().toISOString(), manual: true });
    }, []);

    return (
        <RealTimeContext.Provider value={{ lastUpdate, notifyChange, socket: socketRef.current }}>
            {children}
        </RealTimeContext.Provider>
    );
};
